import { useState } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr, API } from "@/lib/api";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, EmptyState } from "@/components/common";
import { Grid3x3, Plus, AlertOctagon, UploadCloud, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const LEVEL_COLORS = ["", "bg-slate-100 text-slate-600", "bg-sky-100 text-sky-700", "bg-blue-100 text-blue-700", "bg-indigo-100 text-indigo-800", "bg-emerald-600 text-white"];

export default function CompetenceMatrix() {
  const { effectiveRole } = useAuth();
  const canEdit = ["owner", "hr_admin"].includes(effectiveRole);
  const { data, loading, reload } = useApi("/competence/matrix");
  const { data: shortages } = useApi("/competence/shortages");
  const { data: lib } = useApi("/library");
  const { data: cvData } = useApi("/cv");
  const cvEmpIds = new Set((cvData?.cvs || []).map((c) => c.employee_id));
  const matrisDocs = (lib?.documents || []).filter((d) => d.category === "kompetensmatris");
  const [newComp, setNewComp] = useState("");
  const [open, setOpen] = useState(false);
  const [mOpen, setMOpen] = useState(false);
  const [mText, setMText] = useState("");

  const importMatrix = async () => {
    try {
      const r = await api.post("/competence/matrix/import-csv", { csv_text: mText });
      toast.success(`Matris importerad: ${r.data.levels_set} nivåer · ${r.data.competences_created} nya kompetenser · ${r.data.employees_created} nya medarbetare`);
      setMText(""); setMOpen(false); reload();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const onMatrixFile = async (e) => {
    const f = e.target.files?.[0];
    if (f) setMText(await f.text());
  };

  const addComp = async () => {
    try { await api.post("/competences", { name: newComp }); setNewComp(""); setOpen(false); reload(); toast.success("Kompetens tillagd"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setLevel = async (employee_id, competence_id, level) => {
    try { await api.post("/employee-competences", { employee_id, competence_id, level }); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (loading) return <p className="text-muted-foreground">Laddar…</p>;
  const emps = data?.employees || [];
  const comps = data?.competences || [];
  const shortageIds = new Set((shortages || []).filter((s) => s.has_shortage).map((s) => s.station_id));

  return (
    <div>
      <PageHeader title="Kompetensmatris" icon={Grid3x3}
        subtitle="Nivå 1–5 per medarbetare och kompetens. Endast kvalificerad personal kan föreslås till stationer med krav.">
        {canEdit && (
          <>
            <Dialog open={mOpen} onOpenChange={setMOpen}>
              <DialogTrigger asChild><Button variant="outline" data-testid="upload-matrix-btn"><UploadCloud className="mr-1 h-4 w-4" />Ladda upp matris</Button></DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader><DialogTitle>Ladda upp kompetensmatris (CSV)</DialogTitle></DialogHeader>
                <p className="text-xs text-muted-foreground">Första kolumnerna identifierar medarbetare (t.ex. <code>namn</code> eller <code>extern_ref</code>), övriga kolumnrubriker tolkas som kompetenser med nivå 1–5. Nya kompetenser och medarbetare skapas automatiskt.</p>
                <label className="cursor-pointer rounded-md border px-3 py-2 text-sm hover:bg-secondary w-fit">
                  Välj CSV-fil <input type="file" accept=".csv,text/csv" className="hidden" onChange={onMatrixFile} data-testid="matrix-file" />
                </label>
                <Textarea rows={8} className="font-mono text-xs" value={mText} onChange={(e) => setMText(e.target.value)}
                  placeholder={"namn,Truckkort A,Plock,Packning\nAnna Ek,4,3,2\nBo Nord,2,5,4"} data-testid="matrix-textarea" />
                <DialogFooter><Button onClick={importMatrix} disabled={!mText.trim()} data-testid="run-matrix">Importera matris</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="add-competence-btn"><Plus className="mr-1 h-4 w-4" />Ny kompetens</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Ny kompetens</DialogTitle></DialogHeader>
              <Label htmlFor="ncn">Namn</Label>
              <Input id="ncn" value={newComp} onChange={(e) => setNewComp(e.target.value)} data-testid="new-competence-name" />
              <DialogFooter><Button onClick={addComp} data-testid="save-competence">Spara</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          </>
        )}
      </PageHeader>

      {matrisDocs.length > 0 && (
        <Card className="mb-4 p-4" data-testid="matrix-docs">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><FileText className="h-4 w-4 text-accent" />Uppladdade matrisdokument ({matrisDocs.length})</div>
          <div className="space-y-2">
            {matrisDocs.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`matrix-doc-${d.id}`}>
                <span className="font-medium">{d.title}</span>
                {d.industry && <span className="rounded-full bg-secondary px-2 py-0.5 text-xs">{d.industry}</span>}
                {d.file_path && <a href={`${API}/files/${d.file_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent underline"><ExternalLink className="h-3.5 w-3.5" />Öppna</a>}
                <Link to="/bibliotek" className="ml-auto text-xs text-muted-foreground underline">Hantera i Biblioteket</Link>
              </div>
            ))}
          </div>
        </Card>
      )}
      {shortages && shortageIds.size > 0 && (
        <Card className="mb-4 flex items-start gap-3 border-red-200 bg-red-50 p-4">
          <AlertOctagon className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
          <div className="text-sm">
            <span className="font-semibold text-red-800">Kompetensbrist:</span>{" "}
            {[...shortageIds].length} station(er) saknar tillräckligt kvalificerad personal för obligatoriska krav.
          </div>
        </Card>
      )}

      {emps.length === 0 || comps.length === 0 ? (
        <EmptyState icon={Grid3x3} title="Matrisen är tom"
          text="Lägg till kompetenser och medarbetare för att bygga upp matrisen." />
      ) : (
        <Card className="overflow-x-auto kr-scroll p-0">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b bg-secondary/50">
                <th className="sticky left-0 z-10 bg-secondary/50 px-4 py-3 text-left font-semibold">Medarbetare</th>
                {comps.map((c) => <th key={c.id} className="min-w-[110px] px-3 py-3 text-left font-medium text-xs">{c.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {emps.map((e) => (
                <tr key={e.id} className="border-b hover:bg-secondary/30" data-testid={`matrix-row-${e.id}`}>
                  <td className="sticky left-0 z-10 bg-card px-4 py-2 font-medium">
                    <span className="inline-flex items-center gap-1.5">{e.first_name} {e.last_name}
                      {cvEmpIds.has(e.id) && <Link to="/cv" title="CV finns" data-testid={`matrix-cv-${e.id}`}><FileText className="h-3.5 w-3.5 text-accent" /></Link>}
                    </span>
                  </td>
                  {comps.map((c) => {
                    const lvl = data.levels?.[e.id]?.[c.id] || 0;
                    return (
                      <td key={c.id} className="px-3 py-2">
                        {canEdit ? (
                          <select value={lvl} onChange={(ev) => setLevel(e.id, c.id, Number(ev.target.value))}
                            data-testid={`matrix-cell-${e.id}-${c.id}`}
                            className={`w-14 rounded-md border px-2 py-1 text-center text-sm font-semibold ${LEVEL_COLORS[lvl] || "text-muted-foreground"}`}>
                            <option value={0}>–</option>
                            {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                          </select>
                        ) : (
                          <span className={`inline-flex h-7 w-7 items-center justify-center rounded-md text-xs font-bold ${LEVEL_COLORS[lvl] || "text-muted-foreground"}`}>{lvl || "–"}</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
